import { EngagementKeys } from '../../common/engagementKeys';
import {
  loadLivePersonAssets,
  loadCookieGatedCoreAssets,
  loadBrandAssets,
  loadChatUiAssets
} from '../../utils/asset-loader-utils';

import { LiveChatError } from '../../utils/common-utils';

function isExcludedRoute(currentPath: string, excludedRoutes: string[]): boolean {
  return excludedRoutes.some((pattern) => {
    const regexPattern = pattern.replace(/\*/g, '[^ ]*');
    const regex = new RegExp(regexPattern);
    return regex.test(currentPath);
  });
}

export async function loadUiResources(
  manifest: Manifest,
  resources: Resources,
  bucketPath: string
) {
  if (resources.ui?.enabled) {
    const excludedRoutes = resources.ui.excluded_routes || [];
    const globalExclusions = ['*/checkout*', '*/cart*', '*/iframe*'];
    excludedRoutes.push(...globalExclusions);

    const currentPath = window.location.pathname;

    if (!isExcludedRoute(currentPath, excludedRoutes)) {
      // Check if container already exists to prevent duplicates
      let liveChatContainer = document.getElementById('live-chat-ui-container');
      if (!liveChatContainer) {
        // Create the containing div
        liveChatContainer = document.createElement('div');
        liveChatContainer.id = 'live-chat-ui-container';
        document.body.appendChild(liveChatContainer);
      }

      let liveChatEngagements = document.getElementById('live-chat-engagements');
      if (!liveChatEngagements) {
        liveChatEngagements = document.createElement('div');
        liveChatEngagements.id = 'live-chat-engagements';
        liveChatContainer.appendChild(liveChatEngagements);
      }

      // Create a div for each engagementKey; at runtime, the LivePerson Web Taglet will fill each with a LivePerson HTML Engagement
      Object.keys(EngagementKeys).forEach((engKey: string) => {
        if (!document.getElementById(engKey)) {
          const div = document.createElement('div');
          div.id = engKey;
          liveChatEngagements.appendChild(div);
        }
      });

      // Check if UI assets have already been loaded to prevent duplicate loading
      if (!window.LiveChat?.uiAssetsLoaded) {
        await loadChatUiAssets(manifest.ui, bucketPath);
        // @ts-expect-error - Unpacking window.LiveChat leads TS to expect possible undefined vars. These are verified upstream, however, so we will ignore this check.
        window.LiveChat = {
          ...window.LiveChat,
          uiAssetsLoaded: true
        };
      }
    }
  }
}

function isCookiesAllowed(): boolean {
  return !!window.OptanonActiveGroups && window.OptanonActiveGroups.indexOf('3') !== -1;
}

export async function loadLivePersonResources(
  manifest: Manifest,
  resources: Resources,
  bucketPath: string
) {
  if (resources.liveperson && resources.liveperson.enabled) {
    await loadLivePersonAssets(manifest.liveperson, bucketPath);
    loadCookieGatedCoreAssets(manifest.asset_loader, bucketPath);
  }

  // Register a listener for changes to allowed cookies, and load the LP script if it wasn't loaded before
  window.addEventListener('OneTrustGroupsUpdated', async () => {
    if (
      isCookiesAllowed() &&
      resources!.liveperson.enabled &&
      !(window.lpTag?._tagCount && window.lpTag?._tagCount > 0)
    ) {
      await loadLivePersonAssets(manifest?.liveperson, bucketPath);
    }
  });
}

async function fetchBrandManifest(brandCode: string, environment: string) {
  try {
    const response = await fetch(`/livechat/${environment}/manifests/${brandCode}-manifest.json`);
    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText || ''}}`);
    }

    return response;
  } catch (e: unknown) {
    if (e instanceof Error) {
      console.error(`LiveChat - Could not fetch  brand manifest for ${brandCode}`, e);
    }
    // prevent bubbling up as these are caught in akamai logs
    return;
  }
}

export async function fetchAndLoadBrandAssets({
  resources,
  bucketPath,
  brandCode,
  environment
}: {
  resources: Resources;
  bucketPath: string;
  brandCode: string;
  environment: string;
}) {
  const response = await fetchBrandManifest(brandCode, environment);

  if (response) {
    const brandAssetsManifest = await response.json();
    await loadBrandAssets(resources, brandAssetsManifest, bucketPath);
  }
}

async function assetLoaderInit() {
  if (typeof window === 'undefined' || !window.LiveChat) {
    return;
  }

  const { environment, brandCode, manifest, resources } = window.LiveChat;

  if (!manifest || !resources) {
    throw new LiveChatError(
      'There are files missing from the window.LiveChat object. Cannot load LiveChat.'
    );
  }

  const bucketPath = `/livechat/${environment}`;

  await loadUiResources(manifest, resources, bucketPath);
  await loadLivePersonResources(manifest, resources, bucketPath);
  await fetchAndLoadBrandAssets({ resources, bucketPath, brandCode, environment });
}

assetLoaderInit();
